import http from "../api/apiClient";

export interface ParentMeetingPlan {
  id: string;
  title: string;
  description?: string;
  classroomId?: string;
  classroomName?: string;
  teacherId?: string;
  durationMinutes: number;
  meetingType?: string;
  status: "DRAFT" | "OPEN" | "CLOSED" | "CANCELLED" | string;
}

export interface ParentMeetingSlot {
  id: string;
  planId: string;
  startTime: string;
  endTime: string;
  available: boolean;
  status?: "AVAILABLE" | "RESERVED" | "BOOKED" | "CANCELLED" | string;
  reservedAt?: string;
  reservedUntil?: string;
  reservationExpiresAt?: string;
}

export interface ParentMeetingInvitation {
  id: string;
  planId: string;
  childId: string;
  parentId?: string;
  email: string;
  status: "INVITED" | "BOOKED" | "CANCELLED" | string;
}

export interface PublicParentMeetingInvitation {
  id: string;
  invitationId?: string;
  token?: string;
  planId: string;
  title: string;
  description?: string;
  childName?: string;
  parentName?: string;
  currentEmail?: string;
  prefilledEmail?: string;
  selectedSlot?: ParentMeetingSlot | null;
  availableSlots: ParentMeetingSlot[];
  status?: "INVITED" | "BOOKED" | "CANCELLED" | string;
}

export interface ClassroomSummary {
  id: string;
  uuid?: string;
  className?: string;
  classType?: string;
  grade?: number;
  startDate?: string;
  endDate?: string;
}

export interface ClassroomChildSummary {
  id: string;
  uuid?: string;
  firstname?: string;
  lastname?: string;
  firstName?: string;
  lastName?: string;
  familyId?: string;
  familyUuid?: string;
  familyName?: string;
  parentId?: string;
  parentUuid?: string;
  parentEmail?: string;
  email?: string;
  parent1Email?: string;
  parent2Email?: string;
  parent1Name?: string;
  parent2Name?: string;
  parents?: Array<{
    id?: string;
    uuid?: string;
    firstname?: string;
    lastname?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
}

export interface ClassroomMeetingInviteOption {
  childId: string;
  childName: string;
  familyId: string | null;
  familyName?: string | null;
  familySituation?: "MARRIED" | "COHABITING" | "SEPARATED" | "DIVORCED" | "SINGLE_PARENT" | "WIDOWED" | "OTHER";
  parent1Id?: string | null;
  parent1Name?: string | null;
  parent1Email?: string | null;
  parent2Id?: string | null;
  parent2Name?: string | null;
  parent2Email?: string | null;
  displayName: string;
}

export interface SelectedMeetingInviteOption {
  childId: string;
  familyId?: string | null;
}

export interface ParentMeetingAvailabilityBlockInput {
  date: string;
  startTime: string;
  endTime: string;
}

export interface ParentMeetingRecurringAvailabilityInput {
  enabled: boolean;
  startDate?: string;
  endDate?: string;
  dayOfWeek?: string;
  startTime?: string;
  endTime?: string;
}

export interface CreateParentMeetingPlanInput {
  classroomId: string;
  meetingType: string;
  title: string;
  description?: string;
  durationMinutes: number;
  targetMode: "CLASSROOM" | "SPECIFIC" | "SELECTED";
  familyIds: string[];
  selectedChildren?: SelectedMeetingInviteOption[];
  availabilityBlocks: ParentMeetingAvailabilityBlockInput[];
  recurringAvailability: ParentMeetingRecurringAvailabilityInput;
}

function normalizeArray<T>(data: T[] | { content?: T[]; items?: T[]; data?: T[] }): T[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export async function listParentMeetingPlans(): Promise<ParentMeetingPlan[]> {
  const { data } = await http.get("/parent-meetings");
  return normalizeArray<ParentMeetingPlan>(data);
}

export async function getClassroom(classroomId: string): Promise<ClassroomSummary> {
  const { data } = await http.get(`/classrooms/${classroomId}`);
  return data;
}

export async function listClassroomChildren(classroomId: string): Promise<ClassroomChildSummary[]> {
  const { data } = await http.get(`/classrooms/${classroomId}/children`);
  return normalizeArray<ClassroomChildSummary>(data);
}

export async function getClassroomMeetingInviteOptions(classroomId: string): Promise<ClassroomMeetingInviteOption[]> {
  const response = await http.get<ClassroomMeetingInviteOption[] | { content?: ClassroomMeetingInviteOption[]; items?: ClassroomMeetingInviteOption[]; data?: ClassroomMeetingInviteOption[] }>(
    `/classrooms/${classroomId}/meeting-invite-options`
  );
  return normalizeArray<ClassroomMeetingInviteOption>(response.data);
}

export async function createParentMeetingPlan(input: CreateParentMeetingPlanInput): Promise<ParentMeetingPlan> {
  const { data } = await http.post("/parent-meetings", input);
  return data;
}

export async function addAvailabilityBlock(
  planId: string,
  input: { startTime: string; endTime: string }
): Promise<ParentMeetingSlot[]> {
  const { data } = await http.post(`/parent-meetings/${planId}/availability-blocks`, input);
  return normalizeArray<ParentMeetingSlot>(data);
}

export async function createInvitation(
  planId: string,
  input: { childId: string; parentId?: string; email: string }
): Promise<ParentMeetingInvitation> {
  const { data } = await http.post(`/parent-meetings/${planId}/invitations`, {
    childUuid: input.childId,
    parentUuid: input.parentId,
    parentEmail: input.email,
  });
  return data;
}

export async function openPlan(planId: string): Promise<ParentMeetingPlan> {
  const { data } = await http.post(`/parent-meetings/${planId}/open`);
  return data;
}

export async function getPublicInvitation(token: string): Promise<PublicParentMeetingInvitation> {
  const { data } = await http.get(`/parent-meetings/invitations/${token}`);
  return {
    ...data,
    id: data.id || data.invitationId,
    currentEmail: data.currentEmail || data.prefilledEmail || "",
    availableSlots: normalizeArray<ParentMeetingSlot>(data.availableSlots || []),
  };
}

export async function confirmPublicInvitation(
  token: string,
  input: { slotId: string; email: string }
): Promise<PublicParentMeetingInvitation> {
  const { data } = await http.post(`/parent-meetings/invitations/${token}/confirm`, {
    slotId: input.slotId,
    confirmedEmail: input.email
  });
  return data;
}


export async function reservePublicInvitationSlot(
  token: string,
  input: { slotId: string }
): Promise<ParentMeetingSlot> {
  const { data } = await http.post(`/parent-meetings/invitations/${token}/slots/${input.slotId}/reserve`, {});
  return data;
}
